import { useContext } from "react"
import * as PropTypes from "prop-types"
import styled from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import Col from "components/styled/Grid/Col"
import { Row } from "components/styled/Grid"
import Typography from "components/styled/Typography"
import { numberWithDots } from "helpers/formatter"
import isFunction from "lodash/isFunction"
import { CAR_COLORS } from "helpers/constants"
import { color as c } from "helpers/styled"
import { CarContext } from "../CarContext"

const Background = styled(Row)`
  margin-top: 25px;
  margin-bottom: 25px;
`

const Label = styled(Typography)`
  margin-top: 10px;
  text-transform: uppercase;
  white-space: nowrap;
`

const ColorContainer = styled.div`
  display: flex;
  margin-right: -5px;
`

const Color = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 5px;
  text-transform: capitalize;

  &:before {
    content: "";
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 5px;
    background: ${({ color }) => color};
    border: 1px solid ${c("silver")};
    border-radius: 12px;
  }
`

export default function Specs() {
  const { car, selectedPlan } = useContext(CarContext)
  const t = useT()
  const locale = useLocale()

  const {
    averageFuelConsumption,
    color,
    currentKm,
    cylinder,
    humanizedDeliveryProvinces,
    fuel,
    height,
    length,
    loadspaceCapacity,
    flexibleStatus,
    warrantyYears,
    range,
    registrationYear,
    width,
    sellingStatus,
    maintenanceText
  } = car

  const deliveryProvinces = humanizedDeliveryProvinces?.join(", ")
  const hasDimensions = [width, height, length].every(
    value => value !== null && value !== undefined && Number(value) > 0
  )

  return (
    <Background>
      {color?.length > 0 && (
        <Item
          title={t("car_data.available_colors")}
          value={() => (
            <ColorContainer>
              {color.map(x => (
                <Color key={x} color={CAR_COLORS[x]}>
                  <Typography variant="small">{t(`car_data.color.${x}`)}</Typography>
                </Color>
              ))}
            </ColorContainer>
          )}
        />
      )}
      <Item title={t("car_data.specs.trunk")} value={loadspaceCapacity} units={t("car.units.liters")} />
      <Item
        title={t("car_data.specs.engine-size")}
        value={cylinder && `${Math.round(cylinder * 0.01) / 10} L / ${numberWithDots(cylinder)} cm3`}
      />
      {hasDimensions && (
        <Item
          title={t("car_data.specs.dimensions")}
          value={t("car_data.specs.dimensions-data", { width, height, length })}
        />
      )}
      <Item title={t("car_data.specs.range")} value={range} units="km" />
      <Item
        title={t("car_data.specs.consumption")}
        value={averageFuelConsumption}
        units={fuel === "electric" ? "kWh/100km" : t("car.units.consumption")}
      />
      <Item title={t("car_data.specs.registration")} value={registrationYear} />
      {sellingStatus === "pre-owned" && locale === "es" && (
        <Item
          title={t("car_data.specs.delivery-province")}
          value={deliveryProvinces}
          footnote={t("car_data.specs.delivery-province-footnote")}
        />
      )}

      {sellingStatus === 'flexible' &&
        <>
          {selectedPlan.flexibleEndPrice && <Item title={t("car_data.specs.price_with_taxes")}
            value={selectedPlan.flexibleEndPrice.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })} />}
          <Item title={t("car_data.specs.registration_year")} value={registrationYear} />
          <Item title={t("car_data.specs.warranty_years")} value={warrantyYears} />
          <Item title={t("car_data.specs.current-km")} value={currentKm} units="km" />
          <Item title={t("car_data.specs.flexible_status")} value={t(`car_data.specs.flexible_status_types.${flexibleStatus}`)} />
          <Item title={t("car_data.specs.maintenance")} value={maintenanceText} />

        </>
      }

    </Background>
  )
}

const Footnote = styled(Typography)`
  margin-top: 5px;
  font-style: italic;
`

function Item({ title, value, units, footnote, allowZero }) {
  const isRenderableFunction = isFunction(value)
  const isZeroValue = !isRenderableFunction && (value === 0 || value === "0")
  const hasContent =
    isRenderableFunction || (value !== null && value !== undefined && value !== "")
  const shouldShow = title && title !== "" && hasContent && (!isZeroValue || allowZero)

  if (!shouldShow) return null

  return (
    <Col xs={12} sm={6} style={{ marginBottom: "10px" }}>
      <Label variant="small" color="gray">
        {title}
      </Label>
      {isFunction(value) ? (
        value()
      ) : (
        <Typography weight="bold">{`${value}${units ? ` ${units}` : ""}${footnote ? "*" : ""}`}</Typography>
      )}
      {footnote && <Footnote variant="small">*{footnote}</Footnote>}
    </Col>
  )
}

Item.defaultProps = {
  value: null,
  units: null,
  footnote: null,
  allowZero: false
}

Item.propTypes = {
  title: PropTypes.string.isRequired,
  units: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.func]),
  footnote: PropTypes.string,
  allowZero: PropTypes.bool
}
